import { useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { apiFetch, apiMutate } from "@/lib/api";
import type { UiPrefs } from "@shared/ui-prefs";
import { errorStatus } from "@/lib/api-error";
import { showNotice } from "@/lib/notice";
import { APP_ICON_PREF_KEY, DEFAULT_APP_ICON, applyAppIcon, isAppIconKey } from "@/lib/app-icon";

// --- Preferences (moved from use-conversations.ts) ---

export interface UserPreferences {
  default_model: string;
  show_tool_calls: number;
  theme: string;
  timezone: string;
  date_format: string;
  time_format: string;
  color_scheme: string;
  digest_email: number;
  anomaly_email: number | null;
  /** #682: per-type opt-in for the QC receipt notify. NULL = inherit the digest setting. */
  receipt_email?: number | null;
  /** JSON blob of per-user UI state (e.g. {"collapsed":{"Production":true}}) — synced across devices. */
  ui_prefs?: string;
  /**
   * #682: the three scheduled email types with availability (a per-type column added by a
   * not-yet-applied migration comes back `available: false` so the UI disables its toggle).
   * Derived server-side from EMAIL_TYPES — same labels as the admin subscription matrix.
   */
  email_types?: EmailTypeMeta[];
}

export interface EmailTypeMeta {
  type: "digest" | "anomaly" | "receipt";
  column: "digest_email" | "anomaly_email" | "receipt_email";
  label: string;
  available: boolean;
}

export function usePreferences() {
  return useQuery({
    queryKey: ["preferences"],
    queryFn: () => apiFetch<UserPreferences>("/api/preferences"),
  });
}

export function useUpdatePreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      data: Partial<Omit<UserPreferences, "show_tool_calls" | "ui_prefs"> & { show_tool_calls?: boolean }>,
    ) => apiMutate<{ ok: boolean }>("/api/preferences", "PUT", data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["preferences"] }),
  });
}

/**
 * #3072: the ONE way the client writes ui_prefs. Send only the slice you own
 * (`{ collapsed: {...} }`, `{ tables: { records: {...} } }`, `{ icc: { p12: {...} } }`,
 * `{ cost_basis: "standard_cost" }`); the server merges it into the stored blob
 * under a compare-and-swap and answers with the whole row.
 *
 * Whole-blob PUT is gone, and with it the failure it caused: two tabs each holding
 * a base read minutes apart, the later save reverting the earlier tab's slice with
 * a 200 and nothing shown to the user.
 *
 * The returned row seeds the cache rather than invalidating it: the server has just
 * told us the authoritative blob, so a refetch would only be a slower way to learn
 * the same thing (and would race the next slice write).
 *
 * A 409 is the server saying its compare-and-swap lost three times: the slice was
 * NOT stored. Every migrated slice is idempotent, so the helper refetches the row
 * (whatever won the race is now in the cache) and re-sends the same slice once.
 * If that fails too, it says so: these writes fire from module scope with a
 * `.catch(() => {})` behind them, and a save that silently did not land looks
 * saved until the next page load shows the old value.
 *
 * Callable outside React (the Sidebar and DataTable sync from module scope), which
 * is why the QueryClient is an argument rather than a hook read. Omitted, it is
 * imported lazily: a static import of the app-wide client pulls @tanstack/react-query
 * into every module that touches this file, and the suites that mock that package
 * partially then fail to import at all.
 */
export const UI_PREFS_SAVE_FAILED = "Your display preference did not save. Try again.";

export interface PatchUiPrefsOptions {
  /** False for a caller that renders its own error beside the control (MixDesigns). */
  notify?: boolean;
}

export async function patchUiPrefs(
  slice: UiPrefs,
  qc?: QueryClient,
  options: PatchUiPrefsOptions = {},
): Promise<UserPreferences> {
  const client = qc ?? (await import("@/lib/query-client")).queryClient;
  const send = async () => {
    const row = await apiMutate<UserPreferences>("/api/preferences", "PATCH", { ui_prefs: slice });
    client.setQueryData(["preferences"], row);
    return row;
  };

  try {
    return await send();
  } catch (err) {
    if (errorStatus(err) !== 409) {
      if (options.notify !== false) showNotice(UI_PREFS_SAVE_FAILED);
      throw err;
    }
    try {
      await client.refetchQueries({ queryKey: ["preferences"] });
      return await send();
    } catch (retryErr) {
      if (options.notify !== false) showNotice(UI_PREFS_SAVE_FAILED);
      throw retryErr;
    }
  }
}

/**
 * patchUiPrefs for component callers that want pending/error state. Pass
 * `{ notify: false }` when the component renders its own message beside the
 * control, so one failure does not appear twice (MixDesigns ICC save).
 */
export function usePatchUiPrefs(options: PatchUiPrefsOptions = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (slice: UiPrefs) => patchUiPrefs(slice, qc, options),
  });
}

// --- Profile (via better-auth /api/auth/update-user) ---

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { name: string }) => {
      const res = await fetch("/api/auth/update-user", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => {
      // Refetch session to pick up updated name
      qc.invalidateQueries({ queryKey: ["session"] });
    },
  });
}

// --- Password (via better-auth /api/auth/change-password) ---

// --- Sessions (via better-auth /api/auth/list-sessions and /api/auth/revoke-session) ---

export interface SessionInfo {
  id: string;
  token: string;
  createdAt: string;
  expiresAt: string;
  ipAddress: string | null;
  userAgent: string | null;
}

export function useSessions() {
  return useQuery({
    queryKey: ["sessions"],
    queryFn: async () => {
      const res = await fetch("/api/auth/list-sessions", {
        method: "GET",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to list sessions");
      return res.json() as Promise<SessionInfo[]>;
    },
  });
}

export function useRevokeSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (token: string) => {
      const res = await fetch("/api/auth/revoke-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ token }),
      });
      if (!res.ok) throw new Error("Failed to revoke session");
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sessions"] }),
  });
}

// --- App icon (#PWA home-screen icon, user-selectable) ---

/**
 * Applies the user's chosen home-screen icon to the live document.
 *
 * Stored inside the `ui_prefs` JSON blob (same mechanism as the sidebar collapse
 * state and the cost basis) rather than its own column, so it syncs across devices
 * with no migration. Mount once, high in the tree.
 */
export function useAppIcon(): string {
  const { data: prefs } = usePreferences();
  const key = useMemo(() => {
    try {
      const parsed = JSON.parse(prefs?.ui_prefs || "{}");
      if (isAppIconKey(parsed?.[APP_ICON_PREF_KEY])) return parsed[APP_ICON_PREF_KEY] as string;
    } catch {
      // A corrupt blob must not cost the user their icon: fall through to the default.
    }
    return DEFAULT_APP_ICON;
  }, [prefs?.ui_prefs]);
  useEffect(() => {
    applyAppIcon(key);
  }, [key]);
  // #4974: the key, for the install bar's tile.
  return key;
}
